import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Scale, ArrowRight } from "lucide-react";
import ContactModal from "./ContactModal";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if mobile menu is open — mobile uses same state for nested dropdowns
      if (mobileMenuOpen) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
        setActiveSubCategory(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setActiveSubCategory(null);
    // Scroll to top when location changes
    window.scrollTo(0, 0);
  }, [location]);

  const servicesMenu = {
    "Trademark Services": {
      services: [
        {
          name: "Register Your Trademark",
          path: "/services/trademark/register",
          description: "Secure your brand name and logo",
        },
        {
          name: "Clearance Search Services",
          path: "/services/trademark/clearance",
          description: "Verify trademark availability",
        },
        {
          name: "Trademark Ownership Transfers",
          path: "/services/trademark/ownership-transfers",
          description: "Transfer trademark rights",
        },
        {
          name: "Managed Trademark Renewal",
          path: "/services/trademark/renewal",
          description: "Keep your trademark active",
        },
        {
          name: "Submission of Statements of Use",
          path: "/services/trademark/statement-of-use",
          description: "File intent-to-use statements",
        },
        {
          name: "Support and Response for Trademark Office Actions",
          path: "/services/trademark/office-actions",
          description: "Handle USPTO responses",
        },
        {
          name: "Active Monitoring for Trademark Infringement",
          path: "/services/trademark/monitoring",
          description: "Protect against infringement",
        },
        {
          name: "Support and Guidance for Trademark Cancellations",
          path: "/services/trademark/cancellations",
          description: "Navigate cancellation proceedings",
        },
        {
          name: "Legal Representation for Trademark Disputes",
          path: "/services/trademark/disputes",
          description: "Expert legal representation",
        },
        {
          name: "TTAB Petition",
          path: "/services/trademark/ttab",
          description: "Trademark trial proceedings",
        },
        {
          name: "Combatting Counterfeiting and Trademark Infringement",
          path: "/services/trademark/counterfeiting",
          description: "Stop counterfeiters",
        },
      ],
      image:
        "https://images.unsplash.com/photo-1645484686977-dbddd9e1dc0a?crop=entropy&cs=srgb&fm=jpg&w=800&h=600&fit=crop",
    },
    "Copyright Services": {
      services: [
        {
          name: "Register Your Copyright",
          path: "/services/copyright/register",
          description: "Protect your creative works",
        },
        {
          name: "Assistance for Your Copyright Enforcement",
          path: "/services/copyright/enforcement",
          description: "Enforce your copyright rights",
        },
        {
          name: "Copyright Transfers and Licensing Support",
          path: "/services/copyright/transfers",
          description: "License and transfer rights",
        },
        {
          name: "Legal Representation for Your Copyright",
          path: "/services/copyright/litigation",
          description: "Copyright litigation support",
        },
        {
          name: "Management of Your Copyright Portfolio",
          path: "/services/copyright/portfolio",
          description: "Manage multiple copyrights",
        },
        {
          name: "Compliance with DMCA",
          path: "/services/copyright/dmca",
          description: "Digital copyright protection",
        },
      ],
      image:
        "https://images.unsplash.com/photo-1761746395622-5f7e0e7b4ec7?crop=entropy&cs=srgb&fm=jpg&w=800&h=600&fit=crop",
    },
    "Patent Services": {
      services: [
        {
          name: "Search for Existing Patent",
          path: "/services/patent/search",
          description: "Prior art searches",
        },
        {
          name: "Support and Response for Patent Office Actions",
          path: "/services/patent/office-actions",
          description: "USPTO office action responses",
        },
        {
          name: "Application Submission for Patent",
          path: "/services/patent/application",
          description: "File patent applications",
        },
        {
          name: "Legal Representation for Patent Protection",
          path: "/services/patent/litigation",
          description: "Patent enforcement",
        },
      ],
      image:
        "https://images.pexels.com/photos/19895784/pexels-photo-19895784.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    "Company Formation": {
      services: [
        {
          name: "Limited Liability Company (LLC)",
          path: "/services/company/llc",
          description: "Form your LLC quickly",
        },
        {
          name: "C Corporation",
          path: "/services/company/c-corp",
          description: "Incorporate as C Corp",
        },
        {
          name: "S Corporation",
          path: "/services/company/s-corp",
          description: "S Corp formation",
        },
        {
          name: "Non-Profit",
          path: "/services/company/non-profit",
          description: "Start a non-profit organization",
        },
      ],
      image:
        "https://images.unsplash.com/photo-1758873269317-51888e824b28?crop=entropy&cs=srgb&fm=jpg&w=800&h=600&fit=crop",
    },
    "Legal Services": {
      services: [
        {
          name: "Business Immigration Law",
          path: "/services/legal/immigration",
          description: "Immigration legal support",
        },
        {
          name: "Due Diligence Process",
          path: "/services/legal/due-diligence",
          description: "Legal due diligence",
        },
        {
          name: "IT & Commercial Law",
          path: "/services/legal/it-commercial",
          description: "Technology and commercial law",
        },
        {
          name: "Legal Consultation on Partnerships & LLPs",
          path: "/services/legal/partnerships",
          description: "Partnership legal advice",
        },
        {
          name: "Data Protection & Privacy Law",
          path: "/services/legal/data-protection",
          description: "Privacy compliance",
        },
      ],
      image:
        "https://images.pexels.com/photos/7841469/pexels-photo-7841469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    "Additional Services": {
      services: [
        {
          name: "Amazon Brand Registry in the US",
          path: "/services/additional/amazon-brand-registry",
          description: "Register on Amazon Brand Registry",
        },
        {
          name: "Bookkeeping Service",
          path: "/services/additional/bookkeeping",
          description: "Professional bookkeeping",
        },
        {
          name: "Business Formation Services",
          path: "/services/additional/business-formation",
          description: "Comprehensive formation services",
        },
        {
          name: "Certificate of Good Standing",
          path: "/services/additional/good-standing",
          description: "Obtain certificates",
        },
        {
          name: "Company Dissolution",
          path: "/services/additional/dissolution",
          description: "Close your business properly",
        },
        {
          name: "Company Name Change",
          path: "/services/additional/name-change",
          description: "Change company name",
        },
        {
          name: "Company Renewal Services",
          path: "/services/additional/renewal",
          description: "Annual renewals",
        },
        {
          name: "Issue of Shares",
          path: "/services/additional/shares",
          description: "Share issuance services",
        },
        {
          name: "Nominee Director & Secretarial Services",
          path: "/services/additional/nominee",
          description: "Nominee services",
        },
        {
          name: "Tax Advisory & Filing",
          path: "/services/additional/tax",
          description: "Tax planning and filing",
        },
        {
          name: "Transfer of Shares",
          path: "/services/additional/transfer-shares",
          description: "Share transfer services",
        },
        {
          name: "CFO Services",
          path: "/services/additional/cfo",
          description: "Chief Financial Officer services",
        },
      ],
      image:
        "https://images.pexels.com/photos/7567551/pexels-photo-7567551.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
  };

  const getButtonText = (category) => {
    const buttonTexts = {
      "Trademark Services": "Get Trademark Pricing",
      "Copyright Services": "Get Copyright Pricing",
      "Patent Services": "Get Patent Pricing",
      "Company Formation": "Get Formation Pricing",
      "Legal Services": "Get Legal Pricing",
      "Additional Services": "View Service Pricing",
    };
    return buttonTexts[category] || "View Pricing";
  };

  const handlePricingClick = (category) => {
    setContactModalOpen(true);
    setActiveDropdown(null);
    setActiveSubCategory(null);
  };

  const menuItems = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Services", hasMegaMenu: true },
    {
      name: "Media Center",
      dropdown: [
        { name: "Blogs", path: "/resources/blogs" },
        { name: "News & Updates", path: "/resources/news" },
        { name: "Publications", path: "/resources/publications" },
      ],
    },
    { name: "Contact Us", path: "/contact" },
  ];

  return (
    <nav className="bg-[#07382f]/95 backdrop-blur-xl text-white sticky top-0 z-50 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex justify-between items-center h-16">
          {/* Brand Name */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src="/assets/img/brand/logo-light.png"
              alt="Trademark Facility"
              className="h-8 sm:h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div
            className="hidden lg:flex items-center space-x-1"
            ref={dropdownRef}
          >
            {menuItems.map((item, index) => (
              <div key={index} className="relative">
                {item.hasMegaMenu ? (
                  <div>
                    <button
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === index ? null : index,
                        )
                      }
                      onMouseEnter={() => setActiveDropdown(index)}
                      className="px-3 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-all duration-200 flex items-center"
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    {activeDropdown === index && (
                      <div
                        className="fixed left-0 right-0 top-16 w-full bg-white shadow-2xl text-slate-900 z-50 border-t border-gray-100"
                        onMouseLeave={() => {
                          setActiveDropdown(null);
                          setActiveSubCategory(null);
                        }}
                      >
                        <div className="max-w-7xl mx-auto">
                          <div className="grid grid-cols-12 gap-0">
                            {/* Left Side - Main Services */}
                            <div className="col-span-3 bg-slate-50 p-6 border-r border-slate-200">
                              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
                                All Services
                              </h3>
                              <div className="space-y-1">
                                {Object.keys(servicesMenu).map(
                                  (category, catIndex) => (
                                    <button
                                      key={catIndex}
                                      onMouseEnter={() =>
                                        setActiveSubCategory(category)
                                      }
                                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-between group ${
                                        activeSubCategory === category ||
                                        (activeSubCategory === null &&
                                          catIndex === 0)
                                          ? "bg-lime text-white"
                                          : "text-slate-700 hover:bg-slate-200"
                                      }`}
                                    >
                                      <span>{category}</span>
                                      <ArrowRight
                                        className={`h-4 w-4 ${activeSubCategory === category || (activeSubCategory === null && catIndex === 0) ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                                      />
                                    </button>
                                  ),
                                )}
                              </div>
                            </div>

                            {/* Middle - Inner Services */}
                            <div className="col-span-5 p-6 max-h-96 overflow-y-auto">
                              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
                                {activeSubCategory ||
                                  Object.keys(servicesMenu)[0]}
                              </h3>
                              <div className="space-y-1">
                                {servicesMenu[
                                  activeSubCategory ||
                                    Object.keys(servicesMenu)[0]
                                ].services.map((service, serviceIndex) => (
                                  <Link
                                    key={serviceIndex}
                                    to={service.path}
                                    className="block px-3 py-2.5 rounded-md hover:bg-slate-100 transition-colors group"
                                  >
                                    <div className="font-medium text-slate-900 text-sm group-hover:text-lime transition-colors mb-0.5">
                                      {service.name}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      {service.description}
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>

                            {/* Right Side - Image */}
                            <div className="col-span-4 bg-slate-100 p-6 flex flex-col justify-center">
                              <img
                                src={
                                  servicesMenu[
                                    activeSubCategory ||
                                      Object.keys(servicesMenu)[0]
                                  ].image
                                }
                                alt={
                                  activeSubCategory ||
                                  Object.keys(servicesMenu)[0]
                                }
                                className="w-full h-64 object-cover rounded-lg shadow-md mb-4"
                              />
                              <h3 className="text-lg font-bold text-slate-900 mb-2">
                                {activeSubCategory ||
                                  Object.keys(servicesMenu)[0]}
                              </h3>
                              <p className="text-sm text-slate-600 mb-4">
                                Expert legal services tailored to protect your
                                intellectual property and business interests.
                              </p>
                              {/* Pricing Button */}
                              <button
                                onClick={() =>
                                  handlePricingClick(
                                    activeSubCategory ||
                                      Object.keys(servicesMenu)[0],
                                  )
                                }
                                className="w-full bg-lime hover:bg-lime-dark text-white font-semibold py-2.5 px-5 rounded-md transition-all duration-200 flex items-center justify-center"
                              >
                                <span>
                                  {getButtonText(
                                    activeSubCategory ||
                                      Object.keys(servicesMenu)[0],
                                  )}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : item.dropdown ? (
                  <div>
                    <button
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === index ? null : index,
                        )
                      }
                      className="px-3 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-all duration-200 flex items-center"
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    {activeDropdown === index && (
                      <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-xl py-2 text-[#2d3132]">
                        {item.dropdown.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            className="block px-4 py-2 text-sm hover:bg-slate-100 transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className="px-3 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-all duration-200"
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#07382f] border-t border-white/10">
          <div className="px-2 pt-2 pb-3 space-y-1 max-h-[calc(100vh-64px)] overflow-y-auto">
            {menuItems.map((item, index) => (
              <div key={index}>
                {item.hasMegaMenu ? (
                  <div>
                    <button
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === index ? null : index,
                        )
                      }
                      className="w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-slate-700 transition-colors flex justify-between items-center"
                    >
                      {item.name}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${activeDropdown === index ? "rotate-180" : ""}`}
                      />
                    </button>
                    {activeDropdown === index && (
                      <div className="pl-2 space-y-2 mt-2">
                        {Object.keys(servicesMenu).map((category, catIndex) => (
                          <div key={catIndex}>
                            <button
                              onClick={() =>
                                setActiveSubCategory(
                                  activeSubCategory === category
                                    ? null
                                    : category,
                                )
                              }
                              className="w-full text-left px-3 py-2 rounded-md text-sm font-semibold hover:bg-slate-700 transition-colors flex justify-between items-center text-lime-300"
                            >
                              {category}
                              <ChevronDown
                                className={`h-3 w-3 transition-transform ${activeSubCategory === category ? "rotate-180" : ""}`}
                              />
                            </button>
                            {activeSubCategory === category && (
                              <div className="pl-4 space-y-1 mt-1">
                                {servicesMenu[category].services.map(
                                  (service, serviceIndex) => (
                                    <Link
                                      key={serviceIndex}
                                      to={service.path}
                                      className="block px-3 py-2 rounded-md text-xs hover:bg-slate-700 transition-colors"
                                    >
                                      {service.name}
                                    </Link>
                                  ),
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : item.dropdown ? (
                  <div>
                    <button
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === index ? null : index,
                        )
                      }
                      className="w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-slate-700 transition-colors flex justify-between items-center"
                    >
                      {item.name}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${activeDropdown === index ? "rotate-180" : ""}`}
                      />
                    </button>
                    {activeDropdown === index && (
                      <div className="pl-4 space-y-1 mt-1">
                        {item.dropdown.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            className="block px-3 py-2 rounded-md text-sm hover:bg-slate-700 transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className="block px-3 py-2 rounded-md text-base font-medium hover:bg-slate-700 transition-colors"
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact Modal */}
      <ContactModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        title="Get Started with Trademark Facility"
      />
    </nav>
  );
};

export default Navigation;
